// charts.js

import { getLoadingElement } from './loadingIndicator.js';
import { applyForecastVisibility } from './forecast-chart-plugin.js';

const charts = {};
let chartCounter = 0;
let chartFetchAbortController = new AbortController();

const _datasetNameChartKey = 'chartKey';
const _datasetNameTickCounter = 'chartTickCounter';

/**
 * Determines whether a canvas should fetch data for the current interval event.
 *
 * Two optional HTML attributes control reload throttling (mutually exclusive):
 *
 *   data-chart-minutes="2,3,17,18,32,33,47,48"
 *     Reload only when the current clock minute is in the list. Intended for
 *     endpoints whose data is produced by a cron job on a fixed schedule.
 *
 *   data-chart-interval="5"
 *     Reload every N interval-events (~60 s each), counting from the last
 *     non-interval event (date-change or visibility). data-chart-minutes takes
 *     precedence if both attributes are present.
 *
 * When neither attribute is present the canvas always reloads on every event.
 *
 * @param {HTMLCanvasElement} canvas
 * @returns {boolean}
 */
const _shouldFetchOnInterval = (canvas) => {
  // --- data-chart-minutes ---
  const minutesAttr = canvas.getAttribute('data-chart-minutes');
  if (minutesAttr !== null) {
    const currentMinute = new Date().getMinutes();
    const allowed = minutesAttr.split(',').map(m => parseInt(m.trim(), 10));
    return allowed.includes(currentMinute);
  }

  // --- data-chart-interval (tick counter) ---
  const intervalAttr = canvas.getAttribute('data-chart-interval');
  if (intervalAttr !== null) {
    const every = parseInt(intervalAttr, 10);
    const currentTick = parseInt(canvas.dataset[_datasetNameTickCounter] ?? '0', 10);
    const nextTick = currentTick + 1;
    canvas.dataset[_datasetNameTickCounter] = String(nextTick);

    if (nextTick < every) {
      return false;
    }
    canvas.dataset[_datasetNameTickCounter] = '0';
    return true;
  }

  // No throttle attribute – always fetch.
  return true;
};

/**
 * Updates all charts.
 *
 * @param {string} date    – selected date (YYYY-MM-DD)
 * @param {string} reason  – 'date-change' | 'visibility' | 'interval'
 */
const updateCharts = (date, reason) => {
  chartFetchAbortController.abort();
  chartFetchAbortController = new AbortController();
  const signal = chartFetchAbortController.signal;

  document.querySelectorAll('canvas[data-chart]').forEach(canvas => {
    const config = canvas.querySelector('script[type="application/json"]');
    if (config === null) {
      return;
    }

    const url = canvas.getAttribute('data-chart');
    if (url === undefined) {
      return;
    }

    if (reason === 'interval') {
      if (!_shouldFetchOnInterval(canvas)) {
        return;
      }
    } else {
      // date-change or visibility: always fetch and reset the tick counter.
      canvas.dataset[_datasetNameTickCounter] = '0';
    }

    // --- Initialise or retrieve the Chart.js instance ---
    let chart;
    const eChartKey = canvas.dataset[_datasetNameChartKey];
    if (eChartKey === undefined) {
      chartCounter++;
      const key = 'chart' + chartCounter;
      const chartConfig = JSON.parse(config.innerHTML);
      chart = new Chart(canvas, chartConfig);
      charts[key] = chart;
      canvas.dataset[_datasetNameChartKey] = key;
    } else {
      chart = charts[eChartKey];
    }

    // --- Fetch and update ---
    try {
      const loadingElement = getLoadingElement(canvas.parentElement);

      const separator = url.includes('?') ? '&' : '?';
      fetch(url + separator + 'date=' + date, {signal})
        .then(response => {
          if (!response.ok) {
            throw new Error('HTTP error ' + response.status);
          }
          return response.json();
        })
        .then(json => {
          if (json['labels']) {
            chart['data']['labels'] = json['labels'];
          }
          if (json['datasets']) {
            for (let i = 0; i < chart['data']['datasets'].length; i++) {
              chart['data']['datasets'][i]['data'] = json['datasets'][i]['data'];
            }
          }
          if (json['options']?.['scales']?.['x']?.['min']) {
            chart['options']['scales']['x']['min'] = json['options']['scales']['x']['min'];
          }
          if (json['options']?.['scales']?.['x']?.['max']) {
            chart['options']['scales']['x']['max'] = json['options']['scales']['x']['max'];
          }

          if (json['options']?.['hasForecast'] !== undefined) {
            applyForecastVisibility(chart, json['options']['hasForecast']);
          }

          chart.update();
        })
        .catch(e => {
          if (e.name === 'AbortError') {
            return;
          }
          console.error(e);
        })
        .finally(() => {
          if (!signal.aborted) {
            loadingElement?.classList.remove('show');
          }
        });

    } catch (e) {
      console.warn('Chart failure.');
      console.error(e);
    }
  });
};

document.addEventListener('dateChange', (e) => {
  updateCharts(e.detail.date, e.detail.reason);
});
